// src/pages/Search.js
// Page résultats de recherche - affiche les vraies données uniquement - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ListingGrid from '../components/listing/ListingGrid';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';

const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const lat = searchParams.get('lat');
  const lng = searchParams.get('lng');
  const query = searchParams.get('q');
  const isGeoSearch = lat && lng;

  useEffect(() => {
    const loadResults = async () => {
      setLoading(true);
      setError(false);
      try {
        let listings = [];

        if (isGeoSearch) {
          const result = await apiClient.get(
            `/endpoints/listings.php?action=nearby&lat=${lat}&lng=${lng}&radius=25`
          );
          listings = result.listings || [];
        } else {
          const result = await apiClient.get('/endpoints/listings.php?action=list&status=published&limit=100');
          const all = result.listings || [];
          if (query) {
            const q = query.toLowerCase();
            listings = all.filter(
              (l) =>
                l.title?.toLowerCase().includes(q) ||
                l.quartier?.toLowerCase().includes(q) ||
                l.city?.toLowerCase().includes(q)
            );
          } else {
            listings = all;
          }
        }

        setResults(listings);
      } catch (err) {
        setError(true);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [lat, lng, query, isGeoSearch]);

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.textPrimary,
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px',
    },
    title: { fontSize: '18px', fontWeight: '700', color: theme.textPrimary },
    subtitle: { padding: '8px 16px', fontSize: '14px', color: theme.textSecondary },
    stateMessage: { textAlign: 'center', padding: '48px 24px', color: theme.textSecondary, fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Résultats de recherche | Fynho</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>
          {isGeoSearch ? `🧭 ${t('home.useLocation')}` : t('common.search')}
        </div>
      </div>

      {!loading && !error && (
        <div style={styles.subtitle}>
          {results.length} {results.length > 1 ? t('home.listings') : t('home.listing')}
          {isGeoSearch && results.length > 0 && ' · triées par proximité'}
        </div>
      )}

      {error ? (
        <div style={styles.stateMessage}>{t('errors.networkError')}</div>
      ) : (
        <ListingGrid listings={results} loading={loading} />
      )}
    </div>
  );
};

export default Search;